import { useMemo, useState } from 'react';
import {
  Card, Text, Title, Group, Stack, Loader, Center, Badge, Tabs, Table, Button, Avatar,
  SimpleGrid, Modal, TextInput, Textarea, PasswordInput, Anchor, NumberInput, Select, FileButton, SegmentedControl, Divider,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { IconArrowLeft, IconUserPlus, IconCash, IconLink, IconPencil, IconTrash, IconCamera, IconKey, IconExternalLink, IconChecklist, IconCalendarCheck, IconScale } from '@tabler/icons-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api, { fileUrl } from '../services/api';
import { confirmDelete } from '../lib/confirm';
import { useClasses } from '../hooks/useLookups';
import { PhoneInput } from '../components/PhoneInput';
import { GenderInput } from '../components/QuickCreate';
import { ContactsManager } from '../components/ContactsManager';
import { SectionCard } from '../components/SectionCard';
import { BlockLoader } from '../components/BlockLoader';
import { StudentContracts } from '../components/StudentContracts';
import { ContractButton } from '../components/ContractGenerator';
import { formatSom, formatDate, STUDENT_STATUS_KEYS } from '../lib/format';
import { monthLabel, thisMonth } from '../lib/month';
import type { StudentBalancesResp } from '../lib/balance';
import { BalanceStatusBadge } from '../components/BalanceStatusBadge';
import { useAuth } from '../contexts/AuthContext';
import { canAccess } from '../lib/permissions';
import { uzDateProps } from '../lib/dateInput';
import { modals } from '@mantine/modals';

// Davomat holatlari (Attendance.tsx dagi ro'yxat bilan bir xil)
const ATT_STATUSES = ['PRESENT', 'ABSENT', 'LATE', 'EXCUSED'];

interface SiblingRef { id: string; fullName: string; status: string; photo?: string | null; class?: { name: string } | null }
interface ParentRef { id: string; fullName: string; phone?: string; login?: string; _count?: { children: number }; children?: SiblingRef[] }
interface ContactPhone { id: string; label: string; number: string; isPrimary: boolean }
interface ContactAddress { id: string; label: string; address: string; isPrimary: boolean }
interface StudentFull {
  id: string; fullName: string; phone?: string; address?: string; status: string; gender?: string;
  photo?: string | null;
  // birthDate/note kartochka so'rovida allaqachon kelardi, lekin tipda yo'q edi —
  // tahrir oynasi ularni to'ldirishi uchun ochib qo'yildi.
  birthDate?: string | null; note?: string | null;
  parentName?: string; parentPhone?: string; parentId?: string; admissionDate: string;
  user?: { id: string; login: string; fullName: string; isActive: boolean } | null;
  class?: { id?: string; name: string } | null;
  classId?: string | null;
  parent?: ParentRef | null;
  phones?: ContactPhone[];
  addresses?: ContactAddress[];
  tuitionPlan?: { monthlyAmount: number; discount: number } | null;
  payments: { id: string; amount: number; paidAt: string; forMonth?: string }[];
  grades: { id: string; value: number; date: string; subject?: { name: string } }[];
  attendance: { id: string; date: string; status: string }[];
}

/** `DELETE /students/:id` tasdiqsiz chaqirilganda serverdan keladigan javob. */
interface OchirishOqibati {
  error?: string;
  talab?: string;
  oquvchi?: string;
  ochadi?: Record<string, number>;
  egasizQoladi?: { shartnomalar?: number };
}

export default function StudentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { t } = useTranslation();
  const monthNames = t('monthsShort', { returnObjects: true }) as string[];
  const { user } = useAuth();
  // Rolga qarab amal tugmalarini ko'rsatish: canManage — o'quvchi/ota-ona boshqaruvi (MANAGERS guruhi),
  // canFinance — to'lov rejasi (FINANCE guruhi). Teacher/homeroom bu endpointlarni chaqira olmaydi.
  const canManage = canAccess(user?.role, 'admissions');
  const canFinance = canAccess(user?.role, 'payments');
  // Shartnomalar tabi va "Shartnoma yaratish" — ALOHIDA `contracts` bo'limi
  // (`lib/permissions.ts`): qabul ruxsati o'zgarsa shartnoma oqimi tegilmasin.
  const canContracts = canAccess(user?.role, 'contracts');
  const [parentModal, parentHandlers] = useDisclosure(false);
  const [loginModal, loginHandlers] = useDisclosure(false);
  const [acctModal, acctHandlers] = useDisclosure(false);
  const [linkModal, linkHandlers] = useDisclosure(false);
  const [planModal, planHandlers] = useDisclosure(false);
  const [editModal, editHandlers] = useDisclosure(false);
  const [linkParentId, setLinkParentId] = useState<string | null>(null);
  // "Baholar" tabi uchun mijoz tomonidagi filtrlar (baholar so'rov bilan birga keladi)
  const [gradePeriod, setGradePeriod] = useState<string>('');
  const [gradeSubject, setGradeSubject] = useState<string | null>(null);
  // Davomat/To'lovlar tablari — mijoz tomonidagi filtrlar (yozuvlar kartochka so'rovi bilan keladi)
  const [attStatus, setAttStatus] = useState<string | null>(null);
  const [payYear, setPayYear] = useState<string | null>(null);

  const { data: s, isLoading } = useQuery({
    queryKey: ['student', id],
    queryFn: async () => (await api.get<StudentFull>(`/students/${id}`)).data,
  });

  // Davr boshlanishi — brauzer lokal vaqti bo'yicha (foydalanuvchilar Toshkentda)
  const periodStart = useMemo(() => {
    if (!gradePeriod) return null;
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (gradePeriod === 'day') return start;
    if (gradePeriod === 'week') {
      const shift = (start.getDay() + 6) % 7; // Dushanba — hafta boshi
      start.setDate(start.getDate() - shift);
      return start;
    }
    return new Date(now.getFullYear(), now.getMonth(), 1);
  }, [gradePeriod]);

  const gradeSubjectOptions = useMemo(
    () => Array.from(new Set((s?.grades ?? []).map((g) => g.subject?.name).filter((n): n is string => !!n))).sort(),
    [s?.grades]
  );

  const visibleGrades = useMemo(
    () => (s?.grades ?? []).filter((g) => {
      if (gradeSubject && g.subject?.name !== gradeSubject) return false;
      if (periodStart && new Date(g.date) < periodStart) return false;
      return true;
    }),
    [s?.grades, gradeSubject, periodStart]
  );

  const visibleAttendance = useMemo(
    () => (s?.attendance ?? []).filter((a) => !attStatus || a.status === attStatus),
    [s?.attendance, attStatus]
  );

  // To'lov yillari — ko'rsatilayotgan yozuvlardan (brauzer lokal vaqti, formatDate bilan bir xil)
  const paymentYears = useMemo(
    () => Array.from(new Set((s?.payments ?? []).map((p) => String(new Date(p.paidAt).getFullYear())))).sort((a, b) => b.localeCompare(a)),
    [s?.payments]
  );
  const visiblePayments = useMemo(
    () => (s?.payments ?? []).filter((p) => !payYear || String(new Date(p.paidAt).getFullYear()) === payYear),
    [s?.payments, payYear]
  );
  const visiblePaymentsSum = useMemo(() => visiblePayments.reduce((n, p) => n + p.amount, 0), [visiblePayments]);

  const { data: parents } = useQuery({
    queryKey: ['parents-list'],
    queryFn: async () => (await api.get<ParentRef[]>('/students/parents')).data,
    enabled: linkModal && canManage,
  });

  // JORIY OY BALANSI (#66) — reja/to'langan/qarz va holat.
  // Hisob MOLIYA bo'limidagi jadval bilan bir xil manbadan (`GET /reports/balances/students`),
  // shu sabab bu yerda qayta hisoblanmaydi: kartochka so'rovi faqat OXIRGI 20 ta to'lovni
  // beradi, ulardan hisoblangan qarz jadvaldagidan farq qilib qolishi mumkin edi.
  // Endpoint FINANCE ruxsatini talab qiladi — o'qituvchi uchun umuman so'ralmaydi.
  const balanceMonth = thisMonth();
  const { data: balanceResp, isLoading: balanceLoading } = useQuery({
    queryKey: ['student-balance', id, balanceMonth],
    queryFn: async () =>
      (await api.get<StudentBalancesResp>('/reports/balances/students', {
        params: { month: balanceMonth, ids: id },
      })).data,
    enabled: !!id && canFinance,
  });
  const bal = balanceResp?.items?.[0] ?? null;

  // Sinflar faqat tahrirlash oynasi ochilganda yuklanadi
  const { data: classes } = useClasses(editModal);

  const linkParent = useMutation({
    mutationFn: () => api.patch(`/students/${id}`, { parentId: linkParentId }),
    onSuccess: () => { notifications.show({ color: 'green', message: t('studentDetailPage.parentLinked') }); qc.invalidateQueries({ queryKey: ['student', id] }); setLinkParentId(null); linkHandlers.close(); },
  });

  const parentForm = useForm({
    initialValues: { fullName: '', login: '', password: '', gender: '', phone: '' },
    validate: {
      fullName: (v) => (!v ? t('studentDetailPage.validateFullName') : null),
      login: (v) => (v.length < 3 ? t('studentDetailPage.validateLogin') : null),
      password: (v) => (v.length < 5 ? t('studentDetailPage.validatePassword') : null),
      gender: (v) => (!v ? t('studentDetailPage.validateGender') : null),
    },
  });

  // O'quvchining O'Z kabinet akkaunti (rol STUDENT) — ota-ona akkauntidan alohida.
  // F.I.Sh va jins berilmasa server o'quvchi kartochkasidan oladi.
  const loginForm = useForm({
    initialValues: { login: '', password: '' },
    validate: {
      login: (v) => (v.length < 3 ? t('studentDetailPage.validateLogin') : null),
      password: (v) => (v.length < 5 ? t('studentDetailPage.validatePassword') : null),
    },
  });
  const createLogin = useMutation({
    mutationFn: (v: typeof loginForm.values) => api.post(`/students/${id}/create-login`, v),
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('studentDetailPage.loginCreated') });
      qc.invalidateQueries({ queryKey: ['student', id] });
      loginForm.reset();
      loginHandlers.close();
    },
    onError: (e: unknown) =>
      notifications.show({ color: 'red', message: (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? t('common.error') }),
  });
  /* MAVJUD akkauntni tahrirlash — `create-login` faqat yaratadi (akkaunt bor bo'lsa
     409). Parolini unutgan o'quvchi uchun panelda boshqa yo'l yo'q edi. Parol bo'sh
     qoldirilsa yuborilmaydi, ya'ni faqat loginni o'zgartirish mumkin. */
  const acctForm = useForm({
    initialValues: { login: '', password: '' },
    validate: {
      login: (v) => (v.trim().length < 3 ? t('studentDetailPage.validateLogin') : null),
      password: (v) => (v && v.length < 5 ? t('studentDetailPage.validatePassword') : null),
    },
  });
  const updateLogin = useMutation({
    mutationFn: (v: typeof acctForm.values) =>
      api.patch(`/students/${id}/login`, { login: v.login.trim(), ...(v.password ? { password: v.password } : {}) }),
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('studentDetailPage.loginUpdated') });
      qc.invalidateQueries({ queryKey: ['student', id] });
      acctForm.setFieldValue('password', '');
      acctHandlers.close();
    },
    onError: (e: unknown) =>
      notifications.show({ color: 'red', message: (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? t('common.error') }),
  });

  // O'quvchi rasmi — Settings.tsx dagi avatar yuklash bilan bir xil yondashuv:
  // multipart "file" → server S3 kalitini qaytaradi, rasm /uploads/file/{key} orqali ko'rsatiladi.
  const photoInvalidate = () => {
    qc.invalidateQueries({ queryKey: ['student', id] });
    qc.invalidateQueries({ queryKey: ['students'] });
  };
  const photoErr = (e: unknown) =>
    notifications.show({ color: 'red', message: (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? t('common.error') });
  const photoMut = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post<{ photo: string }>(`/students/${id}/photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
    onSuccess: () => { notifications.show({ color: 'green', message: t('studentDetailPage.photoUpdated') }); photoInvalidate(); },
    onError: photoErr,
  });
  const photoDelMut = useMutation({
    mutationFn: () => api.delete(`/students/${id}/photo`),
    onSuccess: () => { notifications.show({ color: 'green', message: t('studentDetailPage.photoRemoved') }); photoInvalidate(); },
    onError: photoErr,
  });

  const planForm = useForm({ initialValues: { monthlyAmount: 0, discount: 0 } });
  // Tahrir oynasi kartochkaning O'ZGARTIRILADIGAN barcha maydonlarini qamraydi
  // (egasi, 10.09: «tahrir oynasida ma'lumot juda kam»). Ro'yxat `PATCH /students/:id`
  // qabul qiladigan maydonlar bilan cheklangan: qo'shimcha telefon/manzillar u yerda
  // qo'llanmaydi, ular «Kontaktlar» bo'limida (o'z endpointlari bilan) tahrirlanadi.
  const editForm = useForm({
    initialValues: {
      fullName: '', gender: '', classId: '', status: 'ACTIVE',
      birthDate: null as Date | null, phone: '', parentName: '', parentPhone: '', note: '',
    },
    validate: { fullName: (v) => (v.trim().length < 3 ? t('studentDetailPage.validateFullName') : null) },
  });

  const openEdit = () => {
    if (!s) return;
    editForm.setValues({
      fullName: s.fullName ?? '',
      gender: s.gender ?? '',
      classId: s.class?.id ?? s.classId ?? '',
      status: s.status ?? 'ACTIVE',
      birthDate: s.birthDate ? new Date(s.birthDate) : null,
      phone: s.phone ?? '',
      parentName: s.parentName ?? '',
      parentPhone: s.parentPhone ?? '',
      note: s.note ?? '',
    });
    editHandlers.open();
  };

  const saveEdit = useMutation({
    // Bo'shatilgan maydon `null` bilan yuboriladi, `undefined` bilan emas: server
    // `undefined` ni "tegilmadi" deb tushunadi, ya'ni tozalash jimgina yo'qolib ketardi.
    mutationFn: (v: typeof editForm.values) => api.patch(`/students/${id}`, {
      fullName: v.fullName.trim(),
      gender: v.gender || undefined, // GenderInput tozalanmaydi — bo'sh qiymat "tanlanmagan" degani
      classId: v.classId || null,
      status: v.status,
      birthDate: v.birthDate ? v.birthDate.toISOString() : null,
      phone: v.phone.trim() || null,
      parentName: v.parentName.trim() || null,
      parentPhone: v.parentPhone.trim() || null,
      note: v.note.trim() || null,
    }),
    onSuccess: () => { notifications.show({ color: 'green', message: t('studentDetailPage.saved') }); qc.invalidateQueries({ queryKey: ['student', id] }); qc.invalidateQueries({ queryKey: ['students'] }); editHandlers.close(); },
  });

  const createParent = useMutation({
    mutationFn: (v: typeof parentForm.values) => api.post(`/students/${id}/create-parent`, v),
    onSuccess: () => { notifications.show({ color: 'green', message: t('studentDetailPage.parentCreated') }); qc.invalidateQueries({ queryKey: ['student', id] }); parentForm.reset(); parentHandlers.close(); },
  });
  const savePlan = useMutation({
    mutationFn: (v: typeof planForm.values) => api.put(`/payments/plan/${id}`, v),
    // Reja o'zgarsa qarz ham o'zgaradi — balans kartasi eskirib qolmasin
    onSuccess: () => { notifications.show({ color: 'green', message: t('studentDetailPage.planSaved') }); qc.invalidateQueries({ queryKey: ['student', id] }); qc.invalidateQueries({ queryKey: ['student-balance', id] }); qc.invalidateQueries({ queryKey: ['balances'] }); planHandlers.close(); },
  });
  /* O'CHIRISH IKKI BOSQICHLI.
     O'quvchida to'sadigan bog'lanish yo'q — sxemada hammasi `Cascade`, ya'ni
     o'chirish bilan birga TO'LOVLAR TARIXI, davomat, baholar va to'lov rejasi
     ham yo'qoladi (shartnoma esa egasiz qoladi). Server `?confirm=1` bo'lmasa
     hech narsa o'chirmay, nima yo'qolishini sanab `409` qaytaradi — shu ro'yxat
     ko'rsatilib, ikkinchi tasdiq so'raladi. Nom bilan tasdiqlashning o'zi
     yetarli emas edi: xodim oqibatni bilmasdan tasdiqlardi. */
  const ochirish = (confirm: boolean) => api.delete(`/students/${id}${confirm ? '?confirm=1' : ''}`);
  const deleteMut = useMutation({
    mutationFn: (confirm: boolean) => ochirish(confirm),
    onSuccess: () => { notifications.show({ color: 'green', message: t('studentDetailPage.deleted') }); qc.invalidateQueries({ queryKey: ['students'] }); navigate('/students'); },
    onError: (e: unknown) => {
      const r = (e as { response?: { status?: number; data?: OchirishOqibati } })?.response;
      if (r?.status === 409 && r.data?.talab === 'confirm') return oqibatniKorsat(r.data);
      notifications.show({ color: 'red', message: r?.data?.error ?? t('common.error') });
    },
  });

  /** Server qaytargan oqibat ro'yxatini ko'rsatib, yakuniy tasdiqni so'raydi. */
  const oqibatniKorsat = (d: OchirishOqibati) => {
    /* Nol qatorlar ko'rsatilmaydi — ro'yxat qisqa va o'qilarli bo'lsin. */
    const qatorlar = Object.entries(d.ochadi ?? {}).filter(([, n]) => n > 0);
    const shartnoma = d.egasizQoladi?.shartnomalar ?? 0;
    modals.openConfirmModal({
      title: t('studentDetailPage.deleteImpactTitle'),
      centered: true,
      children: (
        <Stack gap="xs">
          <Text size="sm">{t('studentDetailPage.deleteImpactIntro', { name: d.oquvchi })}</Text>
          {qatorlar.map(([k, n]) => (
            <Text key={k} size="sm">• {t(`studentDetailPage.impact_${k}`)}: <b>{n}</b></Text>
          ))}
          {shartnoma > 0 && (
            <Text size="sm" c="dimmed">{t('studentDetailPage.impactContracts', { n: shartnoma })}</Text>
          )}
          <Text size="sm" fw={600} c="red">{t('studentDetailPage.deleteImpactWarn')}</Text>
        </Stack>
      ),
      labels: { confirm: t('studentDetailPage.deleteAnyway'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteMut.mutate(true),
    });
  };

  if (isLoading) return <BlockLoader variant="card" />;
  if (!s) return <Center h={200}><Text c="dimmed">{t('studentDetailPage.notFound')}</Text></Center>;

  // Kartochka so'rovi har bir tab uchun cheklangan sondagi OXIRGI yozuvni qaytaradi
  // (baholar 20, davomat 30, to'lovlar 20). Filtr faqat shu kesim ustida ishlaydi —
  // shuning uchun aniq izoh + to'liq sahifaga havola ko'rsatiladi.
  const limitedNote = (n: number, to: string) => (
    <Group justify="space-between" align="center" gap="xs" wrap="wrap" mb="xs">
      <Text size="xs" c="dimmed">{t('studentDetailPage.limitedNote', { n })}</Text>
      <Button size="compact-xs" variant="subtle" rightSection={<IconExternalLink size={14} />} onClick={() => navigate(to)}>
        {t('studentDetailPage.openFull')}
      </Button>
    </Group>
  );

  return (
    <>
      <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate('/students')} mb="md">
        {t('studentDetailPage.backToList')}
      </Button>

      <Card mb="md">
        <Group justify="space-between" wrap="wrap">
          <Group>
            <Avatar size={64} radius="xl" color="brand" src={fileUrl(s.photo)} alt={s.fullName}>
              {s.fullName[0]?.toUpperCase()}
            </Avatar>
            <Stack gap={2}>
              <Title order={3}>{s.fullName}</Title>
              <Group gap="xs">
                <Badge variant="light">{s.class?.name ?? t('studentDetailPage.noClass')}</Badge>
                <Badge color={s.status === 'ACTIVE' ? 'teal' : 'gray'} variant="dot">{(STUDENT_STATUS_KEYS as readonly string[]).includes(s.status) ? t(`studentStatus.${s.status}`) : s.status}</Badge>
              </Group>
              {/* Tug'ilgan sana kartochkada KO'RINADI: aks holda uni tahrir oynasida
                  o'zgartirgan xodim natijani hech qayerda tekshira olmasdi. */}
              {s.birthDate && <Text size="xs" c="dimmed">{t('studentsPage.birthDate')}: {formatDate(s.birthDate)}</Text>}
              {canManage && (
                <Group gap={4} mt={4}>
                  <FileButton onChange={(f) => f && photoMut.mutate(f)} accept="image/png,image/jpeg,image/webp">
                    {(props) => (
                      <Button {...props} size="compact-xs" variant="light" loading={photoMut.isPending}
                        leftSection={<IconCamera size={14} />}>
                        {s.photo ? t('studentDetailPage.changePhoto') : t('studentDetailPage.uploadPhoto')}
                      </Button>
                    )}
                  </FileButton>
                  {s.photo && (
                    <Button size="compact-xs" variant="subtle" color="red" loading={photoDelMut.isPending}
                      onClick={() => photoDelMut.mutate()}>
                      {t('studentDetailPage.removePhoto')}
                    </Button>
                  )}
                </Group>
              )}
            </Stack>
          </Group>
          <Group>
            {canManage && (
              <Button variant="light" color="gray" leftSection={<IconPencil size={16} />} onClick={openEdit}>{t('studentDetailPage.edit')}</Button>
            )}
            {canManage && !s.parentId && (
              <>
                <Button variant="light" leftSection={<IconUserPlus size={16} />} onClick={parentHandlers.open}>{t('studentDetailPage.newParent')}</Button>
                <Button variant="light" color="teal" leftSection={<IconLink size={16} />} onClick={linkHandlers.open}>{t('studentDetailPage.linkExisting')}</Button>
              </>
            )}
            {/* O'quvchining o'z kabineti — allaqachon yaratilgan bo'lsa tugma o'rniga login ko'rsatiladi */}
            {canManage && (s.user
              ? (
                <Button
                  variant="light"
                  color="indigo"
                  leftSection={<IconKey size={16} />}
                  onClick={() => { acctForm.setValues({ login: s.user!.login, password: '' }); acctHandlers.open(); }}
                >
                  {t('studentDetailPage.hasLogin', { login: s.user.login })}
                </Button>
              )
              : <Button variant="light" color="indigo" leftSection={<IconKey size={16} />} onClick={loginHandlers.open}>{t('studentDetailPage.createLogin')}</Button>
            )}
            {canFinance && (
              <Button variant="light" leftSection={<IconCash size={16} />} onClick={() => { planForm.setValues({ monthlyAmount: s.tuitionPlan?.monthlyAmount ?? 0, discount: s.tuitionPlan?.discount ?? 0 }); planHandlers.open(); }}>
                {t('studentDetailPage.tuitionPlan')}
              </Button>
            )}
          </Group>
        </Group>
      </Card>

      <SimpleGrid cols={{ base: 1, sm: 3 }} mb="md">
        <Card><Text size="sm" c="dimmed">{t('fields.phone')}</Text><Text fw={500}>{s.phones?.find((p) => p.isPrimary)?.number ?? s.phone ?? '—'}</Text></Card>
        <Card>
          <Text size="sm" c="dimmed">{t('studentDetailPage.parent')}</Text>
          {s.parent ? (
            <>
              <Group gap="xs">
                <Text fw={500}>{s.parent.fullName}</Text>
                <Badge size="sm" variant="light" color="teal">{t('studentDetailPage.accountChildren', { n: s.parent._count?.children ?? 1 })}</Badge>
              </Group>
              <Text size="sm">{s.parent.phone ?? ''}</Text>
              {(s.parent.children ?? []).filter((c) => c.id !== s.id).length > 0 && (
                <>
                  <Text size="xs" c="dimmed" mt={6}>{t('studentDetailPage.otherChildren')}</Text>
                  <Group gap={6} mt={2}>
                    {(s.parent.children ?? []).filter((c) => c.id !== s.id).map((c) => (
                      <Badge
                        key={c.id}
                        size="sm"
                        variant="outline"
                        color={c.status === 'ACTIVE' ? 'blue' : 'gray'}
                        style={{ cursor: 'pointer', textTransform: 'none' }}
                        onClick={() => navigate(`/students/${c.id}`)}
                      >
                        {c.fullName}{c.class ? ` · ${c.class.name}` : ''}
                      </Badge>
                    ))}
                  </Group>
                </>
              )}
            </>
          ) : (
            <>
              <Text fw={500}>{s.parentName ?? '—'}</Text>
              <Text size="sm">{s.parentPhone ?? ''}</Text>
            </>
          )}
        </Card>
        <Card><Text size="sm" c="dimmed">{t('studentDetailPage.monthlyTuition')}</Text><Text fw={500}>{s.tuitionPlan ? formatSom(s.tuitionPlan.monthlyAmount - s.tuitionPlan.discount) : '—'}</Text></Card>
        {/* Izoh — faqat yozilgan bo'lsa: bo'sh kartochka gridni behuda cho'zardi */}
        {s.note && <Card><Text size="sm" c="dimmed">{t('studentDetailPage.note')}</Text><Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{s.note}</Text></Card>}
      </SimpleGrid>

      {/* Joriy oy balansi — Moliya bo'limidagi "Balanslar" jadvali bilan AYNI
          rang/yorliq tizimida (`lib/balance.ts`) */}
      {canFinance && (
        <SectionCard
          title={t('studentDetailPage.balanceTitle')}
          subtitle={monthLabel(balanceResp?.month ?? balanceMonth, monthNames)}
          icon={IconScale}
          color="grape"
          mb="md"
        >
          {balanceLoading ? (
            <Center h={56}><Loader size="sm" /></Center>
          ) : !bal ? (
            <Text c="dimmed" size="sm">{t('studentDetailPage.balanceEmpty')}</Text>
          ) : (
            <SimpleGrid cols={{ base: 2, sm: 4 }}>
              <Stack gap={2}>
                <Text size="xs" c="dimmed">{t('reports.plan')}</Text>
                <Text fw={700}>{formatSom(bal.due)}</Text>
                {/* Chegirma rejadan ALLAQACHON ayrilgan — ochiq yozmasak "reja nega kichik" savoli qolardi */}
                {bal.discount > 0 && <Text size="xs" c="orange.7">{t('balances.discount')}: −{formatSom(bal.discount)}</Text>}
                {!bal.hasPlan && <Text size="xs" c="dimmed">{t('balances.noPlan')}</Text>}
              </Stack>
              <Stack gap={2}>
                <Text size="xs" c="dimmed">{t('reports.paidCol')}</Text>
                <Text fw={700} c="green.7">{formatSom(bal.paid)}</Text>
              </Stack>
              <Stack gap={2}>
                <Text size="xs" c="dimmed">{bal.balance < 0 ? t('reports.debtCol') : t('balances.overpaid')}</Text>
                <Text fw={700} c={bal.balance < 0 ? 'red.7' : bal.balance > 0 ? 'blue.7' : 'dimmed'}>
                  {bal.balance < 0 ? formatSom(bal.debt) : bal.balance > 0 ? formatSom(bal.overpaid) : '—'}
                </Text>
              </Stack>
              <Stack gap={2}>
                <Text size="xs" c="dimmed">{t('balances.status')}</Text>
                <Group gap={6}><BalanceStatusBadge status={bal.status} size="lg" /></Group>
              </Stack>
            </SimpleGrid>
          )}
        </SectionCard>
      )}

      <Tabs defaultValue="grades">
        <Tabs.List mb="md">
          <Tabs.Tab value="grades">{t('studentDetailPage.tabGrades')}</Tabs.Tab>
          <Tabs.Tab value="attendance">{t('studentDetailPage.tabAttendance')}</Tabs.Tab>
          <Tabs.Tab value="payments">{t('studentDetailPage.tabPayments')}</Tabs.Tab>
          <Tabs.Tab value="contacts">{t('studentDetailPage.tabContacts')}</Tabs.Tab>
          {canContracts && <Tabs.Tab value="contracts">{t('contractList.tab')}</Tabs.Tab>}
        </Tabs.List>

        {canContracts && (
          <Tabs.Panel value="contracts">
            <Group justify="flex-end" mb="sm">
              <ContractButton studentId={s.id} variant="button" />
            </Group>
            <StudentContracts studentId={s.id} />
          </Tabs.Panel>
        )}

        <Tabs.Panel value="contacts">
          <ContactsManager
            owner={{ studentId: s.id }}
            phones={s.phones ?? []}
            addresses={s.addresses ?? []}
            queryKey={['student', id]}
            readOnly={!canManage}
          />
        </Tabs.Panel>

        <Tabs.Panel value="grades">
          {limitedNote(20, '/grades')}
          <Group mb="sm" gap="sm" align="flex-end" wrap="wrap">
            <SegmentedControl
              size="sm"
              value={gradePeriod}
              onChange={setGradePeriod}
              data={[
                { value: '', label: t('gradesPage.periodAll') },
                { value: 'day', label: t('gradesPage.periodDay') },
                { value: 'week', label: t('gradesPage.periodWeek') },
                { value: 'month', label: t('gradesPage.periodMonth') },
              ]}
            />
            <Select
              placeholder={t('gradesPage.allSubjects')}
              data={gradeSubjectOptions.map((name) => ({ value: name, label: name }))}
              value={gradeSubject}
              onChange={setGradeSubject}
              clearable
              searchable
              w={180}
            />
          </Group>
          <SectionCard title={t('common.list')} icon={IconChecklist} p={0} right={<Badge variant="light" color="gray">{visibleGrades.length}</Badge>}>
            {!visibleGrades.length ? (
              <Center h={100}><Text c="dimmed">{(s.grades ?? []).length ? t('studentDetailPage.noMatch') : t('studentDetailPage.noGrades')}</Text></Center>
            ) : (
              <Table verticalSpacing="sm">
                <Table.Thead><Table.Tr><Table.Th>{t('fields.subject')}</Table.Th><Table.Th>{t('studentDetailPage.colGrade')}</Table.Th><Table.Th>{t('fields.date')}</Table.Th></Table.Tr></Table.Thead>
                <Table.Tbody>
                  {visibleGrades.map((g) => (
                    <Table.Tr key={g.id}>
                      <Table.Td>{g.subject?.name}</Table.Td>
                      <Table.Td><Badge color={g.value >= 4 ? 'teal' : g.value === 3 ? 'yellow' : 'red'}>{g.value}</Badge></Table.Td>
                      <Table.Td>{formatDate(g.date)}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </SectionCard>
        </Tabs.Panel>

        <Tabs.Panel value="attendance">
          {limitedNote(30, '/attendance')}
          <Group mb="sm" gap="sm" align="flex-end" wrap="wrap">
            <Select
              placeholder={t('att.allStatuses')}
              data={ATT_STATUSES.map((v) => ({ value: v, label: t(`att.${v}`) }))}
              value={attStatus || null}
              onChange={setAttStatus}
              clearable
              w={170}
            />
          </Group>
          <SectionCard title={t('common.list')} icon={IconCalendarCheck} p={0} right={<Badge variant="light" color="gray">{visibleAttendance.length}</Badge>}>
            {!visibleAttendance.length ? (
              <Center h={100}><Text c="dimmed">{s.attendance.length ? t('studentDetailPage.noMatch') : t('studentDetailPage.noAttendance')}</Text></Center>
            ) : (
              <Table verticalSpacing="sm">
                <Table.Thead><Table.Tr><Table.Th>{t('fields.date')}</Table.Th><Table.Th>{t('fields.status')}</Table.Th></Table.Tr></Table.Thead>
                <Table.Tbody>
                  {visibleAttendance.map((a) => (
                    <Table.Tr key={a.id}><Table.Td>{formatDate(a.date)}</Table.Td><Table.Td>{t(`att.${a.status}`, { defaultValue: a.status })}</Table.Td></Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </SectionCard>
        </Tabs.Panel>

        <Tabs.Panel value="payments">
          {limitedNote(20, '/payments')}
          <Group mb="sm" gap="sm" align="center" wrap="wrap">
            <Select
              placeholder={t('studentDetailPage.year')}
              data={paymentYears.map((y) => ({ value: y, label: y }))}
              value={payYear || null}
              onChange={setPayYear}
              clearable
              w={140}
            />
            <Badge size="lg" variant="light" color="green">
              {t('studentDetailPage.filteredTotal')}: {formatSom(visiblePaymentsSum)}
            </Badge>
          </Group>
          <SectionCard title={t('common.list')} icon={IconCash} p={0} right={<Badge variant="light" color="gray">{visiblePayments.length}</Badge>}>
            {!visiblePayments.length ? (
              <Center h={100}><Text c="dimmed">{s.payments.length ? t('studentDetailPage.noMatch') : t('studentDetailPage.noPayments')}</Text></Center>
            ) : (
              <Table verticalSpacing="sm">
                <Table.Thead><Table.Tr><Table.Th>{t('fields.amount')}</Table.Th><Table.Th>{t('fields.month')}</Table.Th><Table.Th>{t('fields.date')}</Table.Th></Table.Tr></Table.Thead>
                <Table.Tbody>
                  {visiblePayments.map((p) => (
                    <Table.Tr key={p.id}><Table.Td fw={500} c="green.7">{formatSom(p.amount)}</Table.Td><Table.Td>{p.forMonth ?? '—'}</Table.Td><Table.Td>{formatDate(p.paidAt)}</Table.Td></Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </SectionCard>
        </Tabs.Panel>
      </Tabs>

      {canManage && (
        <Card withBorder mt="md" style={{ borderColor: 'var(--mantine-color-red-3)' }}>
          <Group justify="space-between" wrap="nowrap">
            <Text size="sm" c="dimmed">{t('studentDetailPage.deleteHint')}</Text>
            <Button color="red" variant="light" leftSection={<IconTrash size={16} />} onClick={() => confirmDelete(s.fullName, () => deleteMut.mutate(false))} loading={deleteMut.isPending}>
              {t('common.delete')}
            </Button>
          </Group>
        </Card>
      )}

      {canManage && (
        <>
      <Modal opened={parentModal} onClose={parentHandlers.close} title={t('studentDetailPage.parentAccountTitle')} centered>
        <form onSubmit={parentForm.onSubmit((v) => createParent.mutate(v))}>
          <Stack>
            <Text size="sm" c="dimmed">{t('studentDetailPage.parentAccountHint')}</Text>
            <TextInput label={t('fields.parentName')} required {...parentForm.getInputProps('fullName')} />
            <GenderInput {...parentForm.getInputProps('gender')} />
            <TextInput label={t('studentDetailPage.login')} required {...parentForm.getInputProps('login')} />
            <PasswordInput label={t('studentDetailPage.password')} required {...parentForm.getInputProps('password')} />
            <PhoneInput label={t('fields.phone')} {...parentForm.getInputProps('phone')} />
            <Group justify="flex-end"><Button variant="default" onClick={parentHandlers.close}>{t('common.cancel')}</Button><Button type="submit" loading={createParent.isPending}>{t('studentDetailPage.create')}</Button></Group>
          </Stack>
        </form>
      </Modal>

      <Modal opened={loginModal} onClose={loginHandlers.close} title={t('studentDetailPage.studentLoginTitle')} centered>
        <form onSubmit={loginForm.onSubmit((v) => createLogin.mutate(v))}>
          <Stack>
            <Text size="sm" c="dimmed">{t('studentDetailPage.studentLoginHint')}</Text>
            <TextInput label={t('studentDetailPage.login')} required {...loginForm.getInputProps('login')} />
            <PasswordInput label={t('studentDetailPage.password')} required {...loginForm.getInputProps('password')} />
            <Group justify="flex-end">
              <Button variant="default" onClick={loginHandlers.close}>{t('common.cancel')}</Button>
              <Button type="submit" loading={createLogin.isPending}>{t('studentDetailPage.create')}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Modal opened={acctModal} onClose={acctHandlers.close} title={t('studentDetailPage.accountTitle')} centered>
        <form onSubmit={acctForm.onSubmit((v) => updateLogin.mutate(v))}>
          <Stack>
            <TextInput label={t('studentDetailPage.login')} required {...acctForm.getInputProps('login')} />
            <PasswordInput
              label={t('studentDetailPage.newPassword')}
              description={t('studentDetailPage.passwordHint')}
              autoComplete="new-password"
              {...acctForm.getInputProps('password')}
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={acctHandlers.close}>{t('common.cancel')}</Button>
              <Button type="submit" loading={updateLogin.isPending}>{t('common.save')}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Modal opened={linkModal} onClose={linkHandlers.close} title={t('studentDetailPage.linkParentTitle')} centered>
        <Stack>
          <Text size="sm" c="dimmed">{t('studentDetailPage.linkParentHint')}</Text>
          <Select
            label={t('studentDetailPage.parent')}
            placeholder={t('common.search')}
            searchable
            nothingFoundMessage={t('common.noResults')}
            data={(parents ?? []).map((p) => ({ value: p.id, label: `${p.fullName}${p.phone ? ` · ${p.phone}` : ''} (${t('studentDetailPage.childrenCount', { n: p._count?.children ?? 0 })})` }))}
            value={linkParentId}
            onChange={setLinkParentId}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={linkHandlers.close}>{t('common.cancel')}</Button>
            <Button disabled={!linkParentId} loading={linkParent.isPending} onClick={() => linkParent.mutate()}>{t('studentDetailPage.link')}</Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={editModal} onClose={editHandlers.close} title={t('studentDetailPage.editTitle')} size="lg" centered>
        <form onSubmit={editForm.onSubmit((v) => saveEdit.mutate(v))}>
          <Stack>
            <TextInput label={t('fields.fullName')} required {...editForm.getInputProps('fullName')} />
            <Group grow align="flex-start">
              <GenderInput {...editForm.getInputProps('gender')} />
              <DateInput label={t('studentsPage.birthDate')} {...uzDateProps} {...editForm.getInputProps('birthDate')} />
            </Group>
            <Group grow align="flex-start">
              <Select
                label={t('fields.class')}
                placeholder={t('studentDetailPage.selectClass')}
                searchable
                clearable
                data={(classes ?? []).map((c) => ({ value: c.id, label: c.name }))}
                value={editForm.values.classId}
                onChange={(v) => editForm.setFieldValue('classId', v || '')}
              />
              <Select
                label={t('fields.status')}
                data={STUDENT_STATUS_KEYS.map((value) => ({ value, label: t(`studentStatus.${value}`) }))}
                {...editForm.getInputProps('status')}
              />
            </Group>
            <PhoneInput
              label={t('fields.phone')}
              description={t('studentDetailPage.mainPhoneHint')}
              {...editForm.getInputProps('phone')}
            />
            <Divider label={t('studentDetailPage.parent')} labelPosition="center" />
            {/* Bu ikkisi — AKKAUNTSIZ ota-ona kontakti: yuqoridagi kartochka ularni
                faqat akkaunt biriktirilmagan o'quvchida ko'rsatadi. */}
            <Group grow align="flex-start">
              <TextInput label={t('studentsPage.parentNameNoAccount')} {...editForm.getInputProps('parentName')} />
              <PhoneInput label={t('fields.parentPhone')} {...editForm.getInputProps('parentPhone')} />
            </Group>
            <Textarea label={t('studentDetailPage.note')} autosize minRows={2} maxRows={6} {...editForm.getInputProps('note')} />
            <Text size="xs" c="dimmed">{t('studentDetailPage.contactsHint')}</Text>
            <Group justify="flex-end">
              <Button variant="default" onClick={editHandlers.close}>{t('common.cancel')}</Button>
              <Button type="submit" loading={saveEdit.isPending}>{t('common.save')}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
        </>
      )}

      {canFinance && (
      <Modal opened={planModal} onClose={planHandlers.close} title={t('studentDetailPage.tuitionPlan')} centered>
        <form onSubmit={planForm.onSubmit((v) => savePlan.mutate(v))}>
          <Stack>
            <NumberInput label={t('studentDetailPage.monthlyAmount')} thousandSeparator=" " min={0} {...planForm.getInputProps('monthlyAmount')} />
            <NumberInput label={t('studentDetailPage.discount')} thousandSeparator=" " min={0} {...planForm.getInputProps('discount')} />
            <Anchor size="sm" c="dimmed">{t('studentDetailPage.final')}: {formatSom((planForm.values.monthlyAmount || 0) - (planForm.values.discount || 0))}</Anchor>
            <Group justify="flex-end"><Button variant="default" onClick={planHandlers.close}>{t('common.cancel')}</Button><Button type="submit" loading={savePlan.isPending}>{t('common.save')}</Button></Group>
          </Stack>
        </form>
      </Modal>
      )}
    </>
  );
}
